import { useEffect, useState, useCallback } from "react";
import { useAuth } from "../contexts/AuthContext";
import { useSearchParams } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import {
  Share2, Plus, Loader2, X, Calendar, Sparkles,
  Linkedin, Facebook, Instagram, FileText, Clock, CheckCircle2,
  Bot, Settings, Zap, ClipboardList, Newspaper
} from "lucide-react";
import { MarketingPostGenerator } from "../components/MarketingPostGenerator";
import { MarketingCalendar } from "../components/MarketingCalendar";
import { MarketingPostsList } from "../components/MarketingPostsList";
import { MarketingSettings } from "../components/MarketingSettings";
import { AgentChat } from "../components/AgentChat";
import { SocialAutomations } from "../components/SocialAutomations";
import { AgentTasksDashboard } from "../components/AgentTasksDashboard";
import { MarketingBriefs } from "../components/MarketingBriefs";
import PageHeader from "../components/PageHeader";
import { ListSkeleton } from "../components/Skeletons";

const PLATFORM_CONFIG = {
  linkedin: { label: "LinkedIn", icon: Linkedin, color: "#0a66c2" },
  facebook: { label: "Facebook", icon: Facebook, color: "#1877f2" },
  instagram: { label: "Instagram", icon: Instagram, color: "#e1306c" },
};

const STATUS_LABELS = {
  brouillon: "Brouillon",
  en_attente_validation: "En attente",
  valide: "Valide",
  planifie: "Planifie",
  publie: "Publie",
  rejete: "Rejete",
};

function monthStr(d) { return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`; }

export default function MarketingPage() {
  const { axiosAuth } = useAuth();
  const api = axiosAuth();
  const [searchParams] = useSearchParams();
  const [tab, setTab] = useState(searchParams.get("tab") || "calendrier");
  const [posts, setPosts] = useState([]);
  const [stats, setStats] = useState(null);
  const [calData, setCalData] = useState({ posts: [], by_date: {} });
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calDate, setCalDate] = useState(new Date());
  const [showAgent, setShowAgent] = useState(false);

  // Post dialog
  const [showPost, setShowPost] = useState(false);
  const [editPost, setEditPost] = useState(null);
  const [postForm, setPostForm] = useState({
    platforms: ["linkedin"], content: "", hashtags: [], scheduled_at: "", status: "brouillon",
  });
  const [hashInput, setHashInput] = useState("");
  const [saving, setSaving] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [p, s, cal, tpl] = await Promise.all([
        api.get("/marketing/posts"),
        api.get("/marketing/stats"),
        api.get(`/marketing/calendar?month=${monthStr(calDate)}`),
        api.get("/marketing/prompt-templates").catch(() => ({ data: [] })),
      ]);
      setPosts(p.data || []);
      setStats(s.data);
      setCalData(cal.data || { posts: [], by_date: {} });
      setTemplates(tpl.data || []);
    } catch {}
    setLoading(false);
  }, [calDate]);

  useEffect(() => { reload(); }, [calDate, tab]);

  const openNewPost = (scheduledAt = "") => {
    setEditPost(null);
    setPostForm({ platforms: ["linkedin"], content: "", hashtags: [], scheduled_at: scheduledAt, status: "brouillon" });
    setHashInput("");
    setShowPost(true);
  };

  const openEditPost = (p) => {
    setEditPost(p);
    setPostForm({
      platforms: p.platforms || [p.platform || "linkedin"],
      content: p.content || "",
      hashtags: p.hashtags || [],
      scheduled_at: p.scheduled_at || "",
      status: p.status || "brouillon",
    });
    setHashInput("");
    setShowPost(true);
  };

  const togglePostPlatform = (p) => {
    setPostForm(f => ({
      ...f,
      platforms: f.platforms.includes(p) ? f.platforms.filter(x => x !== p) : [...f.platforms, p],
    }));
  };

  const addHash = () => {
    const tag = hashInput.trim().replace(/^#/, "");
    if (tag && !postForm.hashtags.includes(tag)) {
      setPostForm(f => ({ ...f, hashtags: [...f.hashtags, tag] }));
    }
    setHashInput("");
  };

  const savePost = async () => {
    setSaving(true);
    try {
      if (editPost) {
        await api.put(`/marketing/posts/${editPost.post_id}`, postForm);
      } else {
        await api.post("/marketing/posts", postForm);
      }
      setShowPost(false);
      setEditPost(null);
      reload();
    } catch (e) {
      alert(e.response?.data?.detail || "Erreur");
    }
    setSaving(false);
  };

  const tabs = [
    { key: "calendrier", label: "Calendrier", icon: Calendar },
    { key: "posts", label: "Posts", icon: FileText },
    { key: "generateur", label: "Generateur IA", icon: Sparkles },
    { key: "automations", label: "Automations", icon: Zap },
    { key: "agent-tasks", label: "Taches Agent", icon: ClipboardList },
    { key: "briefs", label: "Briefs Hebdo", icon: Newspaper },
    { key: "parametres", label: "Parametres", icon: Settings },
  ];

  return (
    <div className="p-4 md:p-6 max-w-[1400px] mx-auto" data-testid="marketing-page">
      <PageHeader
        icon={<Share2 />}
        title="Réseaux Sociaux"
        description="Calendrier · Posts · Générateur IA · Automations · Briefs hebdo"
        testId="marketing-header"
        actions={(
          <>
            <Button
              onClick={() => { setShowAgent(!showAgent); }}
              variant="outline"
              className={`border-white/10 text-slate-300 hover:bg-white/5 ${showAgent ? "bg-[#10b981]/10 border-[#10b981]/30 text-[#10b981]" : ""}`}
              data-testid="marketing-agent-btn"
            >
              <Bot className="w-4 h-4 mr-1" /> Agent IA
            </Button>
            {(tab === "calendrier" || tab === "posts" || tab === "agent-tasks") && (
              <Button onClick={() => openNewPost()} className="bg-[#10b981] text-white hover:bg-[#059669] font-bold text-sm" data-testid="create-post-btn">
                <Plus className="w-4 h-4 mr-1" /> Post
              </Button>
            )}
          </>
        )}
      />

      {/* Agent IA Panel */}
      {showAgent && (
        <div className="mb-5">
          <AgentChat agentType="marketing" agentName="Agent Marketing" agentColor="#10b981" onClose={() => setShowAgent(false)} />
        </div>
      )}

      {/* Stats */}
      {stats && !showAgent && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-2 mb-5">
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-3">
            <div className="text-[10px] text-slate-500 uppercase">Posts total</div>
            <div className="text-xl font-black text-[#10b981]">{stats.posts?.total || 0}</div>
            <div className="text-[9px] text-slate-500">{stats.posts?.planifies || 0} planifies, {stats.posts?.publies || 0} publies</div>
          </div>
          <div className="bg-[#14101c] border border-white/5 rounded-xl p-3">
            <div className="text-[10px] text-slate-500 uppercase flex items-center gap-1">
              <Clock className="w-3 h-3 text-amber-400" /> En attente
            </div>
            <div className="text-xl font-black text-amber-400">{stats.posts?.en_attente || 0}</div>
            <div className="text-[9px] text-slate-500">a valider</div>
          </div>
          {Object.entries(PLATFORM_CONFIG).map(([k, cfg]) => {
            const Icon = cfg.icon;
            return (
              <div key={k} className="bg-[#14101c] border border-white/5 rounded-xl p-3">
                <div className="text-[10px] text-slate-500 uppercase flex items-center gap-1"><Icon className="w-3 h-3" /> {cfg.label}</div>
                <div className="text-xl font-black" style={{ color: cfg.color }}>{stats.posts?.by_platform?.[k] || 0}</div>
              </div>
            );
          })}
        </div>
      )}

      {/* Tabs */}
      {!showAgent && (
        <>
          <div className="flex gap-1 mb-5 border-b border-white/10 pb-3 overflow-x-auto">
            {tabs.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)} data-testid={`marketing-tab-${t.key}`}
                className={`flex items-center gap-1.5 px-3 py-2 rounded-lg text-xs font-medium whitespace-nowrap transition-colors ${
                  tab === t.key ? "bg-[#10b981] text-white" : "text-slate-400 hover:text-white hover:bg-white/5"
                }`}>
                <t.icon className="w-3.5 h-3.5" /> {t.label}
              </button>
            ))}
          </div>

          {loading && <ListSkeleton items={4} testId="marketing-loading-skeleton" />}

          {!loading && tab === "calendrier" && (
            <MarketingCalendar calDate={calDate} setCalDate={setCalDate} calData={calData}
              onClickDate={(dateStr) => openNewPost(dateStr + "T09:00")} onClickPost={openEditPost} />
          )}

          {!loading && tab === "posts" && (
            <MarketingPostsList posts={posts} api={api} onReload={reload} onEditPost={openEditPost} />
          )}

          {!loading && tab === "generateur" && (
            <MarketingPostGenerator api={api} onPostSaved={reload} templates={templates} />
          )}

          {!loading && tab === "automations" && (
            <SocialAutomations api={api} onPostsCreated={reload} />
          )}

          {!loading && tab === "agent-tasks" && (
            <AgentTasksDashboard api={api} />
          )}

          {!loading && tab === "briefs" && (
            <MarketingBriefs api={api} />
          )}

          {!loading && tab === "parametres" && (
            <MarketingSettings api={api} />
          )}
        </>
      )}

      {/* Post Dialog */}
      <Dialog open={showPost} onOpenChange={(v) => { setShowPost(v); if (!v) setEditPost(null); }}>
        <DialogContent className="bg-[#14101c] border-white/10 text-white max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader><DialogTitle>{editPost ? "Modifier le post" : "Nouveau post"}</DialogTitle></DialogHeader>
          <div className="space-y-3 mt-2">
            <div>
              <label className="text-[10px] text-slate-500 uppercase mb-1.5 block">Plateformes</label>
              <div className="flex gap-2">
                {Object.entries(PLATFORM_CONFIG).map(([k, cfg]) => {
                  const Icon = cfg.icon;
                  const active = postForm.platforms.includes(k);
                  return (
                    <button key={k} onClick={() => togglePostPlatform(k)} data-testid={`post-platform-${k}`}
                      className={`flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-lg text-xs font-bold transition-all ${
                        active ? "text-white shadow-sm" : "bg-[#0b0811] text-slate-500 border border-white/5"
                      }`} style={active ? { background: cfg.color } : {}}>
                      <Icon className="w-3.5 h-3.5" /> {cfg.label}
                    </button>
                  );
                })}
              </div>
            </div>
            <textarea placeholder="Contenu du post..." value={postForm.content}
              onChange={e => setPostForm(f => ({ ...f, content: e.target.value }))}
              className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-sm h-32 resize-none focus:border-[#10b981]/40 focus:outline-none"
              data-testid="post-content" />
            <div>
              <div className="flex flex-wrap gap-1 mb-1.5">
                {postForm.hashtags.map((h, i) => (
                  <span key={i} className="text-[10px] px-2 py-0.5 rounded-full bg-[#10b981]/10 text-[#10b981] flex items-center gap-1">
                    #{h} <button onClick={() => setPostForm(f => ({ ...f, hashtags: f.hashtags.filter(x => x !== h) }))}><X className="w-2.5 h-2.5" /></button>
                  </span>
                ))}
              </div>
              <div className="flex gap-1">
                <Input placeholder="Hashtag" value={hashInput} onChange={e => setHashInput(e.target.value)}
                  className="bg-[#0b0811] border-white/10 text-white text-xs flex-1"
                  onKeyDown={e => e.key === "Enter" && (e.preventDefault(), addHash())} />
                <Button onClick={addHash} size="sm" variant="outline" className="border-white/10 text-slate-400 text-xs">#</Button>
              </div>
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase mb-1 block">Planification</label>
              <Input type="datetime-local" value={postForm.scheduled_at} onChange={e => setPostForm(f => ({ ...f, scheduled_at: e.target.value }))}
                className="bg-[#0b0811] border-white/10 text-white" />
            </div>
            <div>
              <label className="text-[10px] text-slate-500 uppercase mb-1 block">Statut</label>
              <select value={postForm.status} onChange={e => setPostForm(f => ({ ...f, status: e.target.value }))}
                className="w-full bg-[#0b0811] border border-white/10 text-white rounded-lg px-3 py-2 text-sm">
                {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
              </select>
            </div>
            <Button onClick={savePost} disabled={saving || !postForm.content || postForm.platforms.length === 0}
              className="w-full bg-[#10b981] text-white font-bold" data-testid="save-post-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CheckCircle2 className="w-4 h-4 mr-2" />}
              {editPost ? "Modifier" : "Creer"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
